<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />

  <div v-if="!loading" class="container-fluid my-3">
    <!-- Actions bar (hidden on print) -->
    <div class="d-flex align-items-center justify-content-between mb-3 no-print">
      <h4>طباعة الباركود — {{ gradeName }} ({{ students.length }} طالب)</h4>
      <div class="d-flex gap-2">
        <button class="btn btn-danger" v-print="'#barcodePrintArea'">
          <i class="pi pi-file-pdf me-1"></i> تصدير PDF / طباعة
        </button>
        <router-link :to="`/grades/${gradeSlug}/students/barcodes`" class="btn btn-secondary">
          <i class="pi pi-arrow-right me-1"></i> رجوع
        </router-link>
      </div>
    </div>

    <!-- Printable area -->
    <div id="barcodePrintArea">
      <div class="print-title">
        <h2>طلاب الصف {{ gradeName }}</h2>
      </div>

      <div class="barcode-grid">
        <div
          v-for="student in students"
          :key="student.user_id"
          class="barcode-card"
        >
          <BarcodeLabel
            :studentId="student.user_id"
            :barWidth="1.5"
            :barHeight="50"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import BarcodeLabel from '@/components/global/BarcodeLabel.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'StudentsBarcodesPrint',
  components: { Breadcrumb, Loading, BarcodeLabel },

  data() {
    return {
      students: [],
      loading: false,
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    gradeSlug() { return this.$route.params.gradeSlug; },

    grade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          ?? this.gradeStore.allGrades.find(g => g.slug === slug)
          ?? null;
    },

    gradeName() { return this.grade?.name ?? this.gradeSlug ?? ''; },

    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.gradeName, path: `/grades/${this.gradeSlug}/students` },
        { name: 'باركود الطلاب', path: `/grades/${this.gradeSlug}/students/barcodes` },
        { name: 'طباعة الكل', path: this.$route.fullPath },
      ];
    },
  },

  methods: {
    async fetchStudents() {
      this.loading = true;
      const formData = new FormData();
      formData.append('status', 1);
      formData.append('order_by_name', 1);
      if (this.grade?.id) formData.append('academic_year_id', this.grade.id);

      axios.defaults.headers.common['per_page'] = 100000;
      const res = await axios.post('/all_reservations', formData).catch(() => null);
      const raw = res?.data?.data?.data ?? res?.data?.students ?? [];
      this.students = raw.map(s => ({ user_id: s.user_id ?? s.id, name: s.name }));
      this.loading = false;
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length)    await this.gradeStore.fetchAllGrades();
    await this.fetchStudents();
  },
};
</script>

<style scoped>
/* ── Screen layout ── */
.barcode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  padding: 8px;
}

.barcode-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 8px 6px;
  background: #fff;
  overflow: hidden;
}

.print-title {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
}

/* ── Print styles ── */
@media print {
  .no-print {
    display: none !important;
  }

  #barcodePrintArea {
    padding: 0;
  }

  .print-title h2 {
    font-size: 16pt;
    margin-bottom: 10pt;
  }

  .barcode-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6pt;
    padding: 0;
  }

  .barcode-card {
    border: 1pt solid #000;
    border-radius: 3pt;
    padding: 6pt 4pt 4pt;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
  }

  svg {
    max-width: 100% !important;
    height: auto !important;
  }
}
</style>
